import React, { useState, useEffect, useCallback } from 'react';
import API_URL from '../config';
import axios from 'axios';
import SharedNav from './SharedNav';
import './Linehaul.css';

export default function Linehaul({ user, onLogout }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({}); // id -> draft notes value
  const [dupesOnly, setDupesOnly] = useState(false);
  const [search, setSearch] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/linehaul-contacts`, { headers });
      setRows(res.data || []);
    } catch (e) {
      console.error('Error loading linehaul contacts:', e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveNotes = async (id, value) => {
    setSavingKey(id);
    try {
      await axios.patch(`${API_URL}/api/linehaul-contacts/${id}`, { notes: value }, { headers });
      setRows(prev => prev.map(r => r.id === id ? { ...r, notes: value } : r));
    } catch (e) {
      console.error('Error saving note:', e);
    } finally {
      setSavingKey(null);
    }
  };

  const q = search.trim().toLowerCase();
  const displayRows = rows.filter(row => {
    if (dupesOnly && !row.is_possible_duplicate) return false;
    if (!q) return true;
    const hay = `${row.first_name || ''} ${row.last_name || ''} ${row.email || ''} ${row.organisation || ''} ${row.phone || ''} ${row.states || ''}`.toLowerCase();
    return hay.includes(q);
  });

  const dupeCount = rows.filter(r => r.is_possible_duplicate).length;

  return (
    <div className="linehaul-page">
      <SharedNav user={user} onLogout={onLogout} />
      <div className="linehaul-content">
        <h1 className="linehaul-title">Linehaul</h1>
        <p className="linehaul-subtitle">
          {rows.length} imported Linehaul contractor contacts, deduplicated by email.
          {dupeCount > 0 && ` ${dupeCount} flagged as possible duplicates (same name + company, different email).`}
        </p>

        <div className="linehaul-toolbar">
          <input
            type="text"
            className="linehaul-search"
            placeholder="Search name, email, company, phone, state…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <label className="linehaul-dupes-toggle">
            <input type="checkbox" checked={dupesOnly} onChange={e => setDupesOnly(e.target.checked)} />
            Possible duplicates only ({dupeCount})
          </label>
        </div>

        {loading ? (
          <p className="linehaul-empty">Loading…</p>
        ) : displayRows.length === 0 ? (
          <p className="linehaul-empty">No contacts match.</p>
        ) : (
          <div className="linehaul-table-wrap">
            <table className="linehaul-table">
              <thead>
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Email</th>
                  <th>Organisation</th>
                  <th>Phone</th>
                  <th>States</th>
                  <th>Duplicate?</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map(row => {
                  const noteDraft = drafts[row.id] !== undefined ? drafts[row.id] : (row.notes || '');
                  return (
                    <tr key={row.id} className={row.is_possible_duplicate ? 'linehaul-dupe-row' : ''}>
                      <td>{row.first_name || '—'}</td>
                      <td>{row.last_name || '—'}</td>
                      <td>{row.email ? <a href={`mailto:${row.email}`} className="linehaul-email-link">{row.email}</a> : '—'}</td>
                      <td>{row.organisation || '—'}</td>
                      <td>{row.phone || '—'}</td>
                      <td title={row.states || ''}>
                        {row.states ? (row.states.length > 28 ? row.states.slice(0, 28) + '…' : row.states) : '—'}
                      </td>
                      <td className="linehaul-center">
                        {row.is_possible_duplicate ? (
                          <span className="linehaul-pill linehaul-pill-dupe" title={row.duplicate_note || ''}>Possible</span>
                        ) : (
                          <span className="linehaul-pill linehaul-pill-no">No</span>
                        )}
                      </td>
                      <td className="linehaul-editable-cell">
                        <textarea
                          className="linehaul-notes-input"
                          rows={1}
                          value={noteDraft}
                          placeholder="Add note…"
                          onChange={e => setDrafts(prev => ({ ...prev, [row.id]: e.target.value }))}
                          onBlur={e => {
                            const val = e.target.value;
                            if (val !== (row.notes || '')) saveNotes(row.id, val);
                          }}
                        />
                        {savingKey === row.id && <span className="linehaul-saving">Saving…</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
